// deps
import React, { memo, useCallback, useEffect } from "react";

// internal-deps
import { DialogHOC } from "@swiggy-private/core-ui";
import { CloseIcon } from "@cart-ui/widgets/close-icon";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// styles
import { ButtonWrapper, Container, ContentRow, StyledCloseIcon, StyledText, ButtonRow } from "./styles";

// constants
import { ADD_ADDRESS_CONFIGS } from "@cart-ui/constants/Address";
import { POPUP_COPY_BY_VARIANT, POPUP_IMAGE } from "./constants";

// types
import { FreeCashRemovalPopupVariant } from "@swiggy-private/cart-types";

export interface FreeCashRemovalPopupProps {
    variant: FreeCashRemovalPopupVariant;
    closeModal: VoidFunction;
    onSecondaryClick: VoidFunction;
    onPrimaryClick: VoidFunction;
    onImpression?: VoidFunction;
    onClickAnalytics?: (userAction: string) => void;
}

const FreeCashRemovalPopup: React.FC<FreeCashRemovalPopupProps> = ({
    variant,
    closeModal,
    onSecondaryClick,
    onPrimaryClick,
    onImpression,
    onClickAnalytics,
}) => {
    const config = POPUP_COPY_BY_VARIANT[variant];

    useEffect(() => {
        onImpression?.();
    }, [onImpression]);

    const handleSecondaryClick = useCallback(() => {
        onClickAnalytics?.("cancel");
        onSecondaryClick();
    }, [onClickAnalytics, onSecondaryClick]);

    const handlePrimaryClick = useCallback(() => {
        onClickAnalytics?.("proceed");
        onPrimaryClick();
    }, [onClickAnalytics, onPrimaryClick]);

    return (
        <Container>
            <StyledCloseIcon>
                <CloseIcon
                    onClick={closeModal}
                    IconBgColor={ADD_ADDRESS_CONFIGS.CLOSE_ICON.color}
                    IconFillColor={ADD_ADDRESS_CONFIGS.CLOSE_ICON.fill}
                    size={ADD_ADDRESS_CONFIGS.CLOSE_ICON.size}
                />
            </StyledCloseIcon>
            <ContentRow>
                <Image
                    cloudinaryId={POPUP_IMAGE.id}
                    height={`${POPUP_IMAGE.height}px`}
                    width={`${POPUP_IMAGE.width}px`}
                    cloudinaryHeight={POPUP_IMAGE.height}
                    cloudinaryWidth={POPUP_IMAGE.width}
                />
                <div>
                    <StyledText variant="V2.heading2.neutral.black" color="text_Highest_Emphasis">
                        {config.title}
                    </StyledText>
                    <Text variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                        {config.description}
                    </Text>
                </div>
            </ContentRow>

            <ButtonRow>
                <ButtonWrapper>
                    <SuperButton
                        variant="fullwidth"
                        buttonType="secondary"
                        buttonSize="medium"
                        onClick={handleSecondaryClick}
                    >
                        {config.secondaryButtonText}
                    </SuperButton>
                </ButtonWrapper>

                <ButtonWrapper>
                    <SuperButton variant="fullwidth" buttonType="primary" buttonSize="medium" onClick={handlePrimaryClick}>
                        {config.primaryButtonText}
                    </SuperButton>
                </ButtonWrapper>
            </ButtonRow>
        </Container>
    );
};

export default DialogHOC(memo(FreeCashRemovalPopup));
